import { Box, Button, Flex, Input, Stack } from '@chakra-ui/react';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { useState } from 'react';
import { FieldError, RegisterOptions, useForm } from 'react-hook-form';

import { fetchAPI } from '@/api/fetch';
import { apiResetPassword } from '@/api/services';
import { Field } from '@/components/ui/field';
import useAuth from '@/store/auth';

export default function Password() {
  const { accessToken } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState<boolean>(false);

  const passwordSchema = new RegExp(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
  );
  const passwordFormatMessage =
    'Password must be at least 8 characters, including a mix of letters, numbers, and special characters.';

  interface FormValues {
    password: string;
    confirmPassword: string;
  }

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>();

  const handleFormSubmit = async (formData: FormValues) => {
    setSubmitError(false);

    if (formData.password !== formData.confirmPassword) {
      await trigger('confirmPassword');
      return;
    }

    try {
      const response = await fetchAPI<void>({
        request: () =>
          apiResetPassword(accessToken!, {
            password: formData.password
          })
      });
      if (!response.success) {
        console.error('Failed to update password', response.message);
        setSubmitError(true);
      }
    } catch (error) {
      console.error('Failed to update password:', error);
      setSubmitError(false);
    }
  };

  const renderPasswordField = (
    id: string,
    label: string,
    showPasswordState: boolean,
    toggleShowPassword: () => void,
    error: FieldError | undefined,
    placeholder: string,
    validation: RegisterOptions
  ) => (
    <Field
      key={id}
      label={<Box fontSize="16px">{label}</Box>}
      invalid={!!error}
      errorText={
        <Box fontSize="14px" color="error.700">
          {error?.message}
        </Box>
      }
    >
      <Flex alignItems="center" position="relative" w="100%">
        <Input
          readOnly={isSubmitting}
          id={id}
          size="lg"
          placeholder={placeholder}
          type={showPasswordState ? 'text' : 'password'}
          {...register(id as 'password' | 'confirmPassword', {
            ...validation,
            deps: id === 'password' ? undefined : ['password']
          })}
        />
        <Box
          onClick={toggleShowPassword}
          position="absolute"
          right="10px"
          bg="transparent"
          _hover={{ bg: 'transparent', cursor: 'pointer' }}
          _active={{ bg: 'transparent' }}
          _focus={{ boxShadow: 'none' }}
        >
          {showPasswordState ? <EyeSlash size={24} /> : <Eye color="#000" size={24} />}
        </Box>
      </Flex>
    </Field>
  );

  return (
    <form id="updatePasswordForm" onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack direction="column" gap="24px">
        {renderPasswordField(
          'password',
          'New Password',
          showPassword,
          () => setShowPassword(!showPassword),
          errors.password,
          'Enter new password',
          {
            required: 'Password is required.',
            pattern: {
              value: passwordSchema,
              message: passwordFormatMessage
            }
          }
        )}
        {renderPasswordField(
          'confirmPassword',
          'Confirm New Password',
          showConfirmPassword,
          () => setShowConfirmPassword(!showConfirmPassword),
          errors.confirmPassword,
          'Confirm new password',
          {
            required: 'Confirm password is required.',
            validate: (value: string) => value === watch('password') || 'Passwords do not match.'
          }
        )}
        {submitError && (
          <Box color="error.700" fontSize="16px">
            Failed to update password. Please try again.
          </Box>
        )}
        <Flex justify="flex-end" gap="24px" mt="24px">
          <Button
            onClick={() => {
              reset();
            }}
            fontSize="16px"
            bg="background.secondary"
            color="primary.900"
            border="1px solid"
            borderColor="primary.900"
            borderRadius="8px"
            disabled={isSubmitting}
            p="24px 64px"
            w="160px"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            fontSize="16px"
            bg="primary.900"
            color="neutrals.0"
            borderRadius="8px"
            loading={isSubmitting}
            p="24px 64px"
            w="160px"
          >
            Save
          </Button>
        </Flex>
      </Stack>
    </form>
  );
}
